import {
  CENTER_OF_EXCELLENCE,
  NeurodiversityContactCareNavigatorEntryPoint,
  NeurodiversitySearcherType,
  PediatricsAgeGroup,
  PediatricsContactCarePathway,
  TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC,
} from './types'
import { NEURODIVERSITY_TOOLKIT_PAGE_VERSION } from '../common/constants/mixpanelConstants'
import { TREATMENT_OPTIONS, TREATMENT_OPTIONS_TYPE } from '../common/constants/treatmentOptions'
import { PrimaryNeedsWords } from '../searchForCare/constants'
import { TOOLKIT_TYPE } from '../wellnessToolkit/types'

export const isToolkitTypeCenterOfExcellence = (
  toolkit?: TOOLKIT_TYPE | string,
  toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC | string,
): boolean => {
  return (
    Boolean(toolkit) &&
    Boolean(toolkitSubTopic) &&
    Object.values(CENTER_OF_EXCELLENCE).includes(toolkit as CENTER_OF_EXCELLENCE) &&
    Object.values(TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC).includes(
      toolkitSubTopic as TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC,
    )
  )
}

export const isToolkitTypeNeurodiversity = (toolkit?: TOOLKIT_TYPE) => {
  return toolkit && toolkit === CENTER_OF_EXCELLENCE.NEURODIVERSITY
}

// ACCOUNT-4801 - Parents add a child before triage recommends a neuropsych evaluation, so the NP flow is
// approximated as a child search from the neurodiversity toolkit at a customer that offers NP evaluations
export const isNeuropsychEvaluationChildSearch = ({
  toolkitType,
  isNeuropsychologicalEvaluationsEnabled,
}: {
  toolkitType?: TOOLKIT_TYPE
  isNeuropsychologicalEvaluationsEnabled: boolean
}) => isNeuropsychologicalEvaluationsEnabled && !!isToolkitTypeNeurodiversity(toolkitType)

export const isToolkitTypeHealthChallenges = (toolkit?: TOOLKIT_TYPE) => {
  return toolkit && toolkit === CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES
}

export const isToolkitTypeWomensHealth = (toolkit?: TOOLKIT_TYPE) => {
  return toolkit && toolkit === CENTER_OF_EXCELLENCE.WOMENS_HEALTH
}

export const isToolkitTypeManagerHub = (toolkit?: TOOLKIT_TYPE) => {
  return toolkit && toolkit === CENTER_OF_EXCELLENCE.MANAGER_HUB
}

export const isPediatricsChildSearchAgeGroup = (ageGroup: PediatricsAgeGroup) => {
  return ageGroup === PediatricsAgeGroup.CHILD || ageGroup === PediatricsAgeGroup.TEEN
}

/**
 * Determine what kind of searcherType neurodiversity page should show
 * @param isUserTeen
 */
export const getDefaultNeurodiversitySearcherType = (isUserTeen: boolean) => {
  if (isUserTeen) {
    return NeurodiversitySearcherType.TEEN
  }
  return NeurodiversitySearcherType.ADULT
}

export const isNeuropsychologicalEvaluationTreatmentOption = (
  treatmentOption: TREATMENT_OPTIONS_TYPE | null | undefined,
): treatmentOption is TREATMENT_OPTIONS_TYPE =>
  treatmentOption === TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS ||
  treatmentOption === TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS_CHILD

export const getPediatricsContactCarePathway = (
  treatmentOption: TREATMENT_OPTIONS_TYPE | null | undefined,
): PediatricsContactCarePathway | undefined => {
  switch (treatmentOption) {
    case TREATMENT_OPTIONS.PEDIATRIC_BEHAVIORAL_HEALTH_URGENT_CARE:
    case TREATMENT_OPTIONS.PEDIATRIC_BEHAVIORAL_HEALTH_URGENT_CARE_CHILD:
    case TREATMENT_OPTIONS.PEDIATRIC_FACILITIES_COORDINATION:
    case TREATMENT_OPTIONS.PEDIATRIC_FACILITIES_COORDINATION_CHILD:
      return PediatricsContactCarePathway.BHUC
    case TREATMENT_OPTIONS.PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM:
    case TREATMENT_OPTIONS.PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM_CHILD:
      return PediatricsContactCarePathway.VIOP
    default:
      return undefined
  }
}

export const getNeurodiversityToolkitPageVersion = (
  isUserTeen: boolean,
  hideChildSearch: boolean,
): NEURODIVERSITY_TOOLKIT_PAGE_VERSION => {
  if (isUserTeen) {
    return NEURODIVERSITY_TOOLKIT_PAGE_VERSION.TEEN
  }
  if (hideChildSearch) {
    return NEURODIVERSITY_TOOLKIT_PAGE_VERSION.ADULT_ONLY
  }
  return NEURODIVERSITY_TOOLKIT_PAGE_VERSION.ADULT_CHILD
}

export const getNeurodiversitySearcherContextFromTreatmentOption = (
  treatmentOption: TREATMENT_OPTIONS_TYPE,
  isUserTeen: boolean,
): {
  searcherType: NeurodiversitySearcherType
  subTopic: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC | undefined
  contactCareNavEntryPoint: NeurodiversityContactCareNavigatorEntryPoint
} => {
  const isChildTreatmentOption =
    treatmentOption === TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS_CHILD ||
    treatmentOption === TREATMENT_OPTIONS.CAREGIVER_TRAINING_FOR_NEURO ||
    treatmentOption === TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY_CHILD
  const searcherType = isChildTreatmentOption
    ? NeurodiversitySearcherType.CHILD
    : isUserTeen
      ? NeurodiversitySearcherType.TEEN
      : NeurodiversitySearcherType.ADULT
  const subTopicByTreatmentOption: Partial<Record<TREATMENT_OPTIONS_TYPE, TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC>> = {
    [TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS]:
      TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION,
    [TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS_CHILD]:
      TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION_CHILD,
    [TREATMENT_OPTIONS.CAREGIVER_TRAINING_FOR_NEURO]:
      TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_CAREGIVER_TRAINING_FOR_NEURODIVERSE_CHILD,
  }
  const subTopic = subTopicByTreatmentOption[treatmentOption]
  const contactCareNavEntryPoint =
    subTopic != null
      ? NeurodiversityContactCareNavigatorEntryPoint.SELECTED_CARE_OPTION
      : NeurodiversityContactCareNavigatorEntryPoint.NEURODIVERSITY_NAVIGATION
  return { searcherType, subTopic, contactCareNavEntryPoint }
}

const NEURO_PRIMARY_NEEDS: ReadonlySet<PrimaryNeedsWords> = new Set([
  PrimaryNeedsWords.AUTISM_SPECTRUM,
  PrimaryNeedsWords.ADHD,
  PrimaryNeedsWords.DEVELOPMENTAL_CONCERNS,
])

export const resolveNeurodiversityContactEntryPoint = ({
  treatmentOption,
  defaultEntryPoint,
  searcherType,
  primaryNeed,
}: {
  treatmentOption: TREATMENT_OPTIONS_TYPE
  defaultEntryPoint: NeurodiversityContactCareNavigatorEntryPoint
  searcherType: NeurodiversitySearcherType
  primaryNeed?: PrimaryNeedsWords
}): NeurodiversityContactCareNavigatorEntryPoint => {
  const isSupportForNeurodiversity =
    treatmentOption === TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY ||
    treatmentOption === TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY_CHILD
  if (!isSupportForNeurodiversity) {
    return defaultEntryPoint
  }
  if (
    searcherType === NeurodiversitySearcherType.CHILD &&
    primaryNeed != null &&
    NEURO_PRIMARY_NEEDS.has(primaryNeed)
  ) {
    return NeurodiversityContactCareNavigatorEntryPoint.CHILD_SEARCH_FOR_CARE
  }
  return NeurodiversityContactCareNavigatorEntryPoint.SELECTED_CARE_OPTION
}

export const getToolkitSubTopicLabel = (toolkitSubTopic: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC) => {
  switch (toolkitSubTopic) {
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION:
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION_CHILD:
      return 'Neuropsychological evaluations'
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_CAREGIVER_TRAINING_FOR_NEURODIVERSE_CHILD:
      return 'Caregiver Training'
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_AUTISM:
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_AUTISM_CHILD:
      return 'Support for ASD'
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_ADHD:
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_ADHD_CHILD:
      return 'Support for ADHD'
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_OTHER_NEURODIVERSITY_TOPICS:
    case TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_OTHER_NEURODIVERSITY_TOPICS_CHILD:
      return 'Support for other neurodiversity needs'
    default:
      return toolkitSubTopic
  }
}
